/**
 * Global error handler for catching and logging JavaScript errors
 */

// Global error event listener
window.addEventListener('error', (event) => {
  const { error, message, filename, lineno, colno } = event;
  
  // Check if this is a toggle-related error, Cannot set properties error, or variable initialization error
  if (message && (message.includes('toggle') || message.includes('Toggle') || message.includes('dropdown') || message.includes('Dropdown') || message.includes('Cannot set properties') || message.includes('Cannot access') || message.includes('before initialization'))) {
    console.error('🔄 Component/Variable Error Caught:', {
      message,
      error,
      filename,
      line: lineno,
      column: colno,
      stack: error?.stack,
      timestamp: new Date().toISOString()
    });
    
    // Prevent the error from breaking the app
    event.preventDefault();
    
    // Show user-friendly notification instead of breaking the app
    try {
      // Try to import toast dynamically if not available
      if (typeof window.toast !== 'function') {
        // Import toast from react-toastify if available
        const { toast } = require('react-toastify');
        if (toast) {
          window.toast = toast;
        }
      }
      
      if (window.toast && typeof window.toast === 'function') {
        if (message.includes('toggle') || message.includes('Toggle') || message.includes('dropdown') || message.includes('Dropdown')) {
          window.toast('A dropdown component had an issue. Reloading the page...', {
            type: 'info',
            position: 'top-center'
          });
          // Auto-reload for dropdown/toggle errors as they often require a fresh component state
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else {
          window.toast('A component encountered an error. Please refresh if needed.', {
            type: 'warning',
            position: 'top-center'
          });
        }
      } else {
        // Fallback to console log if toast is not available
        console.warn('Component error occurred:', message);
      }
    } catch (toastError) {
      console.warn('Could not show toast notification:', toastError);
    }
    
    // Optional: Send error to logging service
    logErrorToService({
      type: 'component_variable_error',
      message,
      stack: error?.stack,
      location: `${filename}:${lineno}:${colno}`,
      timestamp: new Date().toISOString()
    });
    
    return true; // Prevent default error handling
  }
});

// Global unhandled promise rejection handler
window.addEventListener('unhandledrejection', (event) => {
  const { reason } = event;
  
  if (reason && reason.message && (reason.message.includes('toggle') || reason.message.includes('Toggle') || reason.message.includes('dropdown') || reason.message.includes('Dropdown'))) {
    console.error('🔄 Toggle Promise Rejection:', {
      reason: reason.message,
      stack: reason.stack,
      timestamp: new Date().toISOString()
    });
    
    // Prevent the promise rejection from being logged as unhandled
    event.preventDefault();
  }
});

// Function to safely log errors to external service
function logErrorToService(errorData) {
  try {
    // Replace with your actual error logging service
    // Examples: Sentry, LogRocket, Bugsnag, etc.
    console.log('📊 Logging error to service:', errorData);
    
    // Example: Send to your backend
    // fetch('/api/log-error', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(errorData)
    // }).catch(err => console.warn('Failed to log error:', err));
    
  } catch (err) {
    console.warn('Failed to log error to service:', err);
  }
}

// Utility function to safely call toggle functions
export const safeToggle = (toggleFn, fallback = () => {}) => {
  return (...args) => {
    try {
      if (typeof toggleFn === 'function') {
        return toggleFn(...args);
      } else {
        console.warn('Toggle function is not a function:', toggleFn);
        return fallback(...args);
      }
    } catch (error) {
      console.error('Error in safeToggle:', error);
      return fallback(...args);
    }
  };
};

// Utility to safely access nested object properties
export const safeGet = (obj, path, defaultValue = undefined) => {
  try {
    const keys = path.split('.');
    let result = obj;
    
    for (const key of keys) {
      if (result === null || result === undefined) {
        return defaultValue;
      }
      result = result[key];
    }
    
    return result !== undefined ? result : defaultValue;
  } catch (error) {
    console.warn('Error in safeGet:', error);
    return defaultValue;
  }
};

export default {
  safeToggle,
  safeGet,
  logErrorToService
}; 